import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import InputField from 'src/components/ui/Input';
import Tooltip from 'src/components/ui/Tooltip';

/** Characters Windows rejects in a filename: \\ / : * ? " < > | and control characters. */
const WINDOWS_FORBIDDEN_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001f]/g;

const WINDOWS_FILENAME_ERROR = 'A filename can’t contain \\ / : * ? " < > |';

/** Backend rejects filename stems longer than this. */
const MAX_FILENAME_STEM_LENGTH = 200;

interface FilenameEditorProps {
  name: string;
  extension?: string | null;
  onSave: (name: string) => void;
  onCancel: () => void;
  /** Tailwind size/weight classes for the editable text and the extension. */
  textClassName?: string;
  className?: string;
}

function withDot(extension?: string | null): string {
  if (!extension) return '';
  return extension.startsWith('.') ? extension : `.${extension}`;
}

/** Editable filename stem with a fixed extension inside the field. */
export default function FilenameEditor({
  name,
  extension,
  onSave,
  onCancel,
  textClassName = 'text-sm font-medium',
  className
}: FilenameEditorProps) {
  const suffix = withDot(extension);
  const [draft, setDraft] = useState(name);
  const cancelRef = useRef(false);
  const nameMeasureRef = useRef<HTMLSpanElement>(null);
  const extensionMeasureRef = useRef<HTMLSpanElement>(null);
  const [fieldWidth, setFieldWidth] = useState(0);
  const [extensionWidth, setExtensionWidth] = useState(0);
  const [showForbiddenTooltip, setShowForbiddenTooltip] = useState(false);
  const tooltipTimerRef = useRef<number | null>(null);

  const showForbidden = () => {
    setShowForbiddenTooltip(true);
    if (tooltipTimerRef.current != null) window.clearTimeout(tooltipTimerRef.current);
    tooltipTimerRef.current = window.setTimeout(() => setShowForbiddenTooltip(false), 2000);
  };

  useEffect(
    () => () => {
      if (tooltipTimerRef.current != null) window.clearTimeout(tooltipTimerRef.current);
    },
    []
  );

  useLayoutEffect(() => {
    if (!nameMeasureRef.current) return;
    const measuredExtension = extensionMeasureRef.current?.offsetWidth ?? 0;
    setExtensionWidth(measuredExtension);
    setFieldWidth(nameMeasureRef.current.offsetWidth + measuredExtension + 30);
  }, [draft, suffix]);

  const commit = () => {
    const trimmed = draft.trim().slice(0, MAX_FILENAME_STEM_LENGTH);
    if (trimmed && trimmed !== name) onSave(trimmed);
    onCancel();
  };

  const cancel = () => {
    cancelRef.current = true;
    onCancel();
  };

  return (
    <span
      className={`inline-flex items-center min-w-0 ${className ?? ''}`}
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.stopPropagation()}
    >
      <span className="flex min-w-0 flex-1 items-center">
        <span
          ref={nameMeasureRef}
          className={`pointer-events-none absolute whitespace-pre font-raleway opacity-0 ${textClassName}`}
          aria-hidden
        >
          {draft || ' '}
        </span>
        <span
          ref={extensionMeasureRef}
          className={`pointer-events-none absolute whitespace-pre font-raleway opacity-0 ${textClassName}`}
          aria-hidden
        >
          {suffix}
        </span>
        <span className="relative inline-block max-w-full min-w-0" style={{ width: Math.max(fieldWidth, 32) }}>
          <Tooltip title={WINDOWS_FILENAME_ERROR} open={showForbiddenTooltip} placement="top">
            <span className="inline-block w-full">
              <InputField
                autoFocus
                value={draft}
                aria-label="Filename"
                spellCheck={false}
                maxLength={MAX_FILENAME_STEM_LENGTH}
                onChange={(event) => {
                  const next = event.target.value.replace(WINDOWS_FORBIDDEN_FILENAME_CHARS, '');
                  if (next !== event.target.value) showForbidden();
                  setDraft(next.slice(0, MAX_FILENAME_STEM_LENGTH));
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commit();
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    cancel();
                  }
                }}
                onBlur={() => {
                  if (cancelRef.current) {
                    cancelRef.current = false;
                    return;
                  }
                  commit();
                }}
                className="w-full"
                inputClassName={`${textClassName} text-content-default`}
                style={extensionWidth ? { paddingRight: extensionWidth } : undefined}
              />
            </span>
          </Tooltip>
          {suffix && (
            <span
              className={`pointer-events-none absolute right-[13px] top-1/2 -translate-y-1/2 text-content-subtlest ${textClassName}`}
            >
              {suffix}
            </span>
          )}
        </span>
      </span>
    </span>
  );
}
